import React, { useMemo, useState } from 'react';
import { GameState, NewsItem } from '../../domain/entities';
import { soundService } from '../../services/SoundService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmbulance, faCheckCircle, faArrowRightArrowLeft, faSoccerBall, faMoneyBill, faSeedling, faEnvelope, faTrophy, faSmile, faHand, faRocket } from '@fortawesome/free-solid-svg-icons';
import { NewsModal } from '../modals/NewsModal';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { InboxHelpModal } from '../shared/InboxHelpModal';
import { View } from '../../App';

interface InboxScreenProps {
    gameState: GameState;
    onMarkNewsAsRead: (id?: string) => void;
    pushView: (view: View) => void;
}

export const InboxScreen: React.FC<InboxScreenProps> = ({ gameState, onMarkNewsAsRead, pushView }) => {
    const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
    const [filterType, setFilterType] = useState<'all' | NewsItem['type']>('all');
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 25;

    const hasUnreadNews = useMemo(() => gameState.news.some(n => !n.isRead), [gameState.news]);
    const recentNews = useMemo(() => [...gameState.news].sort((a, b) => (b.season * 100 + b.week) - (a.season * 100 + a.week)), [gameState.news]);

    const filteredNews = useMemo(() => {
        if (filterType === 'all') return recentNews;
        return recentNews.filter(n => n.type === filterType);
    }, [recentNews, filterType]);

    // Pagination
    const totalPages = Math.ceil(filteredNews.length / ITEMS_PER_PAGE);
    const paginatedNews = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredNews.slice(startIndex, endIndex);
    }, [filteredNews, currentPage]);

    const newsTypes: Array<NewsItem['type']> = ['injury', 'recovery', 'transfer', 'result', 'finance', 'youth', 'general', 'cup', 'morale', 'retirement', 'upset'];
    const typeCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        gameState.news.forEach(n => {
            counts[n.type] = (counts[n.type] || 0) + 1;
        });
        return counts;
    }, [gameState.news]);

    const getTypeIcon = (type: NewsItem['type']) => {
        const iconMap: Record<NewsItem['type'], any> = {
            'injury': faAmbulance,
            'recovery': faCheckCircle,
            'transfer': faArrowRightArrowLeft,
            'result': faSoccerBall,
            'finance': faMoneyBill,
            'youth': faSeedling,
            'general': faEnvelope,
            'cup': faTrophy,
            'morale': faSmile,
            'retirement': faHand,
            'upset': faRocket
        };
        return iconMap[type] || faEnvelope;
    };

    const getTypeIconColor = (type: NewsItem['type']): string => {
        const colorMap: Record<NewsItem['type'], string> = {
            'injury': 'var(--negative-color)',
            'recovery': 'var(--positive-color)',
            'transfer': 'var(--info-color)',
            'result': 'var(--accent-color)',
            'finance': 'var(--warning-color)',
            'youth': 'var(--positive-color)',
            'general': 'var(--accent-color)',
            'cup': 'var(--gold-color)',
            'morale': 'var(--positive-color)',
            'retirement': 'var(--text-secondary-color)',
            'upset': 'var(--warning-color)'
        };
        return colorMap[type] || 'var(--accent-color)';
    };

    const handleViewNews = (item: NewsItem) => {
        soundService.playClick();
        setSelectedNews(item);
        if (!item.isRead) onMarkNewsAsRead(item.id);
    };

    return (
        <>
            <AdPlaceholder />
            <div className="main-content inbox-screen">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px', color: 'var(--accent-color)' }} />Inbox</h1>
                    <button
                        className="button"
                        onClick={() => setIsHelpOpen(true)}
                        style={{ padding: '8px 12px', fontSize: '0.9em' }}
                        title="Help"
                    >
                        Help
                    </button>
                </div>
                <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                    Stay up-to-date with all your team's news, transfers, injuries, results, and more.
                </p>

                {/* Controls */}
                <div className="news-feed-controls" style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div>
                            {hasUnreadNews && (
                                <span style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>
                                    {gameState.news.filter(n => !n.isRead).length} unread
                                </span>
                            )}
                        </div>
                        {hasUnreadNews && (
                            <button className="button small" onClick={() => {
                                soundService.playClick();
                                onMarkNewsAsRead();
                            }}>
                                Mark all read
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Tabs */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px', overflowX: 'auto', paddingBottom: '8px' }}>
                    <button
                        className={`button small ${filterType === 'all' ? 'active' : ''}`}
                        onClick={() => {
                            soundService.playClick();
                            setFilterType('all');
                            setCurrentPage(1);
                        }}
                        style={{
                            backgroundColor: filterType === 'all' ? 'var(--accent-color)' : 'var(--primary-color)',
                            color: filterType === 'all' ? 'var(--secondary-accent-color)' : 'var(--text-color)'
                        }}
                    >
                        <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', color: filterType === 'all' ? 'var(--secondary-accent-color)' : 'var(--accent-color)' }} />All ({gameState.news.length})
                    </button>
                    {newsTypes.map(type => {
                        const count = typeCounts[type] || 0;
                        if (count === 0) return null;
                        return (
                            <button
                                key={type}
                                className={`button small ${filterType === type ? 'active' : ''}`}
                                onClick={() => {
                                    soundService.playClick();
                                    setFilterType(type);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    backgroundColor: filterType === type ? 'var(--accent-color)' : 'var(--primary-color)',
                                    color: filterType === type ? 'var(--secondary-accent-color)' : 'var(--text-color)'
                                }}
                            >
                                <FontAwesomeIcon icon={getTypeIcon(type)} style={{ marginRight: '6px', color: filterType === type ? 'var(--secondary-accent-color)' : getTypeIconColor(type) }} />{type.charAt(0).toUpperCase() + type.slice(1)} ({count})
                            </button>
                        );
                    })}
                </div>

                <div className="table-wrapper">
                    <ul className="news-list">
                        {filteredNews.length === 0 ? (
                            <li style={{ textAlign: 'center', padding: '24px', opacity: 0.7 }}>
                                No {filterType === 'all' ? 'news' : filterType} items yet.
                            </li>
                        ) : (
                            paginatedNews.map(item => (
                                <li
                                    key={item.id}
                                    onClick={() => handleViewNews(item)}
                                    className={!item.isRead ? 'unread' : ''}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px',
                                        backgroundColor: !item.isRead ? 'rgba(0, 255, 155, 0.1)' : 'transparent',
                                        borderLeft: !item.isRead ? '4px solid var(--accent-color)' : '4px solid transparent',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                        <FontAwesomeIcon icon={getTypeIcon(item.type)} style={{ fontSize: '1.2em', flexShrink: 0, color: getTypeIconColor(item.type) }} />
                                        <div style={{ minWidth: 0 }}>
                                            <div className="news-title" style={{ fontWeight: !item.isRead ? 'bold' : 'normal' }}>
                                                {item.title}
                                            </div>
                                            <div style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '4px' }}>
                                                {item.message.substring(0, 60)}
                                                {item.message.length > 60 ? '...' : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0, marginLeft: '12px' }}>
                                        <span className="news-date">S{item.season} W{item.week}</span>
                                        {!item.isRead && (
                                            <span style={{ fontSize: '0.7em', color: 'var(--accent-color)', fontWeight: 'bold' }}>NEW</span>
                                        )}
                                    </div>
                                </li>
                            ))
                        )}
                    </ul>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '24px', marginBottom: '16px' }}>
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="button small"
                            style={{
                                opacity: currentPage === 1 ? 0.5 : 1,
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            ← Previous
                        </button>

                        <span style={{ fontSize: '0.9em', opacity: 0.8 }}>
                            Page {currentPage} of {totalPages} ({filteredNews.length} results)
                        </span>

                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="button small"
                            style={{
                                opacity: currentPage === totalPages ? 0.5 : 1,
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Next →
                        </button>
                    </div>
                )}

                {/* News Modal */}
                {selectedNews && (
                    <NewsModal
                        item={selectedNews}
                        onClose={() => setSelectedNews(null)}
                        gameState={gameState}
                        pushView={pushView}
                    />
                )}

                <InboxHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
            </div>
        </>
    );
};
